import { useQueryClient } from '@tanstack/react-query';
import { App, Button, Space } from 'antd';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { QueryKeys } from '@/common/queryKeys';
import { useDepartments } from '@/common/useDepartments';
import type { Order } from '@/generated/graphql-types';
import { useExpressOrders } from './orders/context/ExpressOrdersProvider';
import { useSubscribeToOrdersMulti } from './orders/query/orders.hooks';
import {
  createDepartmentStatusTooltip,
  createDepartmentTooltip,
  groupOrdersByDepartment,
  groupOrdersByDepartmentAndStatus,
  OrdersBadge,
} from './orders/utils/ordersBadgeHelpers';

const RAPPI_DEPARTMENTS = [
  'CHILE-RM',
  'Lima-Rappi',
  'Rappi Colombia',
  'Trujillo',
  'Huancayo',
  'Cusco',
  'Chiclayo',
  'Chimbote',
  'Ate',
];

interface OrderNotificationData {
  id?: number | null;
  shortCode?: string | null;
  departmentId?: number | null;
  department?: { id?: number | null; name?: string | null } | null;
  statusName?: string | null;
}

const showOrderNotification = (
  order: OrderNotificationData,
  type: 'Rappi' | 'DTK EXPRESS',
  notificationApi: ReturnType<typeof App.useApp>['notification'],
  onOpen: (orderData: OrderNotificationData) => void
) => {
  const departmentName = order.department?.name || 'Unknown';
  const btn = (
    <Space size="small">
      <Button type="primary" size="small" onClick={() => onOpen(order)}>
        Open order
      </Button>
      <Button type="link" size="small" onClick={() => notificationApi.destroy()}>
        Clear all
      </Button>
    </Space>
  );
  notificationApi.info({
    message: `New ${type} Order - ${departmentName}`,
    description: `Order ${order.shortCode || order.id} received.`,
    placement: 'topRight',
    btn,
  });
};

export const RealtimeNotify = () => {
  const { notification: notificationApi } = App.useApp();
  const { departments, allDepartmentIds, selectedDepartmentId, setSelectedDepartmentId } =
    useDepartments();
  const { orders: expressNew } = useExpressOrders();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const rappiDepartmentIds = useMemo(() => {
    return departments
      .filter((dept) => RAPPI_DEPARTMENTS.includes(dept.name || ''))
      .map((dept) => dept.id)
      .filter((id): id is number => id !== undefined);
  }, [departments]);

  const { data } = useSubscribeToOrdersMulti({ departmentIds: rappiDepartmentIds });

  const previousDataRef = useRef<Order[]>([]);
  const previousExpressRef = useRef<Order[]>([]);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const queryClient = useQueryClient();

  const openOrderFromNotification = useCallback(
    (order: OrderNotificationData) => {
      notificationApi.destroy();
      const orderId = order.id ?? null;
      if (!orderId) return;

      const departmentId = order.departmentId ?? order.department?.id ?? null;
      const isDepartmentAllowed = departmentId
        ? departments.some((department) => department.id === departmentId)
        : false;
      const nextParams = new URLSearchParams(searchParams);

      nextParams.set('orderId', String(orderId));
      if (isDepartmentAllowed && departmentId) {
        nextParams.set('departmentId', String(departmentId));
      }
      if (order.statusName?.trim()) {
        nextParams.set('statusName', order.statusName.trim());
      }

      navigate({ pathname: '/', search: nextParams.toString() }, { replace: true });

      if (isDepartmentAllowed && departmentId && departmentId !== selectedDepartmentId) {
        setSelectedDepartmentId(departmentId);
      }
    },
    [
      departments,
      navigate,
      searchParams,
      selectedDepartmentId,
      setSelectedDepartmentId,
      notificationApi,
    ]
  );

  useEffect(() => {
    const enableAudio = () => {
      if (!audioRef.current) {
        audioRef.current = new Audio('/notification.wav');
      }
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };

    document.addEventListener('click', enableAudio);
    document.addEventListener('keydown', enableAudio);

    return () => {
      document.removeEventListener('click', enableAudio);
      document.removeEventListener('keydown', enableAudio);
    };
  }, []);

  const orderIdFromUrl = searchParams.get('orderId');

  useEffect(() => {
    if (!data || rappiDepartmentIds.length === 0) return;
    if (data.length > previousDataRef.current.length) {
      const oldDataIds = new Set(previousDataRef.current.map((order) => order.id));
      const newOrders = data.filter((order) => !oldDataIds.has(order.id as number));

      if (newOrders.length > 0 && audioRef.current) {
        audioRef.current.play().catch((error) => {
          console.error('Audio playback failed:', error);
        });
      }

      const ordersToNotify = newOrders.filter((order) => orderIdFromUrl !== String(order.id ?? ''));
      if (ordersToNotify.length > 0) {
        ordersToNotify.forEach((order) => {
          showOrderNotification(order, 'Rappi', notificationApi, openOrderFromNotification);
        });
        queryClient.invalidateQueries({
          predicate: (query) => query.queryKey[0] === QueryKeys.GET_ORDERS,
        });
        queryClient.invalidateQueries({
          queryKey: [QueryKeys.GET_STATUSES],
        });
      }

      previousDataRef.current = data as Order[];
    }
  }, [
    data,
    queryClient,
    rappiDepartmentIds.length,
    openOrderFromNotification,
    notificationApi,
    orderIdFromUrl,
  ]);

  useEffect(() => {
    if (!expressNew || expressNew.length === 0 || allDepartmentIds.length === 0) return;
    if (expressNew.length > previousExpressRef.current.length) {
      const oldIds = new Set(previousExpressRef.current.map((o) => o.id));
      const newOnes = expressNew.filter((o) => !oldIds.has(o.id as number));

      if (newOnes.length > 0 && audioRef.current) {
        audioRef.current.play().catch(() => {});
      }

      const ordersToNotify = newOnes.filter((order) => orderIdFromUrl !== String(order.id ?? ''));
      if (ordersToNotify.length > 0) {
        ordersToNotify.forEach((order) => {
          showOrderNotification(order, 'DTK EXPRESS', notificationApi, openOrderFromNotification);
        });
        queryClient.invalidateQueries({ predicate: (q) => q.queryKey[0] === QueryKeys.GET_ORDERS });
        queryClient.invalidateQueries({ queryKey: [QueryKeys.GET_STATUSES] });
      }

      previousExpressRef.current = expressNew as Order[];
    }
  }, [
    expressNew,
    queryClient,
    allDepartmentIds.length,
    openOrderFromNotification,
    notificationApi,
    orderIdFromUrl,
  ]);

  const rappiByDepartment = useMemo(() => groupOrdersByDepartment(data as Order[]), [data]);
  const expressByDepartmentAndStatus = useMemo(
    () => groupOrdersByDepartmentAndStatus(expressNew as Order[]),
    [expressNew]
  );

  const handleDepartmentClick = useCallback(
    (departmentId: number) => {
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set('departmentId', String(departmentId));
      nextParams.delete('orderId');
      setSearchParams(nextParams, { replace: true });
      setSelectedDepartmentId(departmentId);
    },
    [searchParams, setSearchParams, setSelectedDepartmentId]
  );

  const rappiTooltip = useMemo(
    () => createDepartmentTooltip(rappiByDepartment, handleDepartmentClick),
    [rappiByDepartment, handleDepartmentClick]
  );

  const expressTooltip = useMemo(
    () => createDepartmentStatusTooltip(expressByDepartmentAndStatus, handleDepartmentClick),
    [expressByDepartmentAndStatus, handleDepartmentClick]
  );

  return (
    <>
      {rappiDepartmentIds.length > 0 && data && data.length > 0 && (
        <OrdersBadge label="Rappi" count={data.length} color="blue" tooltip={rappiTooltip} />
      )}
      {expressNew && expressNew.length > 0 && (
        <OrdersBadge
          label="Express"
          count={expressNew.length}
          color="red"
          tooltip={expressTooltip}
        />
      )}
    </>
  );
};
